import React, { useEffect, useState, useCallback } from "react";
import moment from "moment";
import { Routes, Route, useLocation } from "react-router-dom";
import ForgotPassword from "./Components/Screens/Auth/ForgotPassword";
import Login from "./Components/Screens/Auth/Login";
import ResetPassword from "./Components/Screens/Auth/ResetPassword";
import InitialResetPassword from "./Components/Screens/Auth/InitialResetPassword";
import LoginService from "./Services/LoginService";
import Career from "./Components/Screens/Career/Career";
import Main from "./Main/Main";
import secureLocalStorage from "react-secure-storage";
import EvaluationService from "./Services/EvaluationService";
import UserServices from "./Services/UserService";
import EvaluationPopup from "./Components/Layouts/EvaluationPopup";
import DataParser from "./utlits/Dataparser";
import SocketService from "./Services/SocketService";
import Welcome from "./Components/Screens/Welcome/Welcome";

const App = () => {
  const [forceUpdate, setForceUpdate] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState(null);
  const location = useLocation();

  const loggedUser = JSON.parse(secureLocalStorage.getItem("adminInfo"));

  const headId = loggedUser?.user?.userdata?.id;
  const companyId = loggedUser?.user?.userdata?.company_id;
  const isHead = loggedUser?.user?.userdata?.is_head;

  // ✅ Pending evaluations check logic
  const checkPendingEvaluations = useCallback(async () => {
    console.error("🔍 DEBUG: checkPendingEvaluations START");
    console.error("🔍 DEBUG: User Data:", { headId, companyId, isHead });

    if (!headId || !companyId || isHead !== "yes") {
      return;
    }

    try {
      const res = await EvaluationService.getPendingEvaluations(headId, companyId);
      const data = res.data;

      if (!data || !data.hasPending) {
        setShowPopup(false);
        return;
      }

      const { screenBlocked, alreadySkippedToday } = data;
      const isOnEvaluationPage = location.pathname.includes("/evaluation");

      if ((screenBlocked || !alreadySkippedToday) && !isOnEvaluationPage) {
        setPopupData(data);
        setShowPopup(true);
      } else {
        setShowPopup(false);
      }
    } catch (err) {
      console.error("Pending evaluation check failed:", err);
    }
  }, [headId, companyId, isHead]);

  // ✅ Enforce Blocker when navigating
  useEffect(() => {
    if (popupData?.screenBlocked) {
      const publicPaths = ["/", "/career", "/forgot-password", "/reset-password", "/initial-reset-password", "/unauthorized"];
      const isPublicPath = publicPaths.some(path => location.pathname === path || location.pathname.startsWith("/reset-password/"));
      const isOnEvaluationPage = location.pathname.includes("/evaluation");

      if (!isOnEvaluationPage && !isPublicPath) {
        setShowPopup(true);
      } else {
        setShowPopup(false); // Allow them to see the evaluation page or login
      }
    }
  }, [location.pathname, popupData]);

  // Close Bootstrap modals when clicking outside modal-content
  useEffect(() => {
    const handleModalBackdropClick = (e) => {
      const modalEl = e.target.closest('.modal.show');
      if (!modalEl) return;
      if (e.target.closest('.modal-content')) return;
      if (modalEl.getAttribute('data-bs-backdrop') === 'static') return;
      const bsModal = window.bootstrap?.Modal?.getInstance(modalEl);
      if (bsModal) bsModal.hide();
    };
    document.addEventListener('click', handleModalBackdropClick);
    return () => document.removeEventListener('click', handleModalBackdropClick);
  }, []);

  // 🔁 Force update listener
  useEffect(() => {
    const handlePermissionsUpdated = () => {
      // Recalculate loggedUser from storage
      const updatedUser = JSON.parse(secureLocalStorage.getItem("adminInfo"));
      setForceUpdate((prev) => prev + 1);
    };

    window.addEventListener("permissionsUpdated", handlePermissionsUpdated);
    return () => window.removeEventListener("permissionsUpdated", handlePermissionsUpdated);
  }, []);

  // 🕒 Proactive Session Expiry Check (Idle Timeout)
  useEffect(() => {
    // 1. Initialize Last Active Time
    if (!localStorage.getItem("lastActiveTime")) {
      localStorage.setItem("lastActiveTime", new Date().getTime().toString());
    }

    // 2. Activity Listener (Throttled)
    const updateLastActive = () => {
      localStorage.setItem("lastActiveTime", new Date().getTime().toString());
    };

    const throttle = (func, limit) => {
      let inThrottle;
      return function () {
        const args = arguments;
        const context = this;
        if (!inThrottle) {
          func.apply(context, args);
          inThrottle = true;
          setTimeout(() => (inThrottle = false), limit);
        }
      };
    };

    const throttledUpdate = throttle(updateLastActive, 5000); // 🟢 Update max once every 5s

    const handleUserActivity = () => {
      // If user isn't logged in, don't bother tracking
      if (!secureLocalStorage.getItem("adminInfo")) return;
      throttledUpdate();
    };

    window.addEventListener("mousemove", handleUserActivity);
    window.addEventListener("keypress", handleUserActivity);
    window.addEventListener("click", handleUserActivity);
    window.addEventListener("scroll", handleUserActivity);

    // 3. Periodic Check
    const checkSession = () => {
      const adminInfo = JSON.parse(secureLocalStorage.getItem("adminInfo"));
      if (!adminInfo) return; // Don't check if not logged in

      const lastActive = localStorage.getItem("lastActiveTime");
      // Default to 6 hours if timeout mins not set
      const timeoutMins = JSON.parse(secureLocalStorage.getItem("sessionTimeoutMins")) || 360;

      if (lastActive && timeoutMins) {
        const currentTime = new Date().getTime();
        const allowedIdleTime = Number(timeoutMins) * 60 * 1000;
        const timeSinceLastActive = currentTime - Number(lastActive);

        if (timeSinceLastActive > allowedIdleTime) {
          console.log("🕒 Session expired due to inactivity. Logging out...");
          localStorage.removeItem("lastActiveTime");
          LoginService.logout();
          window.location.href = "/";
        }
      }
    };

    // Check every 30 seconds
    const interval = setInterval(checkSession, 30000);
    checkSession(); // Also check on mount

    return () => {
      clearInterval(interval);
      window.removeEventListener("mousemove", handleUserActivity);
      window.removeEventListener("keypress", handleUserActivity);
      window.removeEventListener("click", handleUserActivity);
      window.removeEventListener("scroll", handleUserActivity);
    };
  }, []);

  // 🟢 Socket Listener for Permissions
  // 🟢 Socket Listener for Permissions
  useEffect(() => {
    SocketService.connect();

    const handlePermissionsUpdate = (data) => {
      // Check if the update is for the current user's role and company
      // Relaxed check for company_id to handle string/number mismatch
      if (String(data.company_id) === String(companyId) && data.action === "update_permissions") {
        const currentUserRole = loggedUser?.user?.userdata?.role;
        if (String(data.roleId) === String(currentUserRole)) {
          console.log("🔔 Permissions updated via Socket:", data.permissions);
          // toast.info("Your permissions have been updated!"); // Optional: Visual feedback

          if (loggedUser && loggedUser.user) {
            updateLocalPermissions(data.permissions);
          }
        }
      }
    };

    const handleEvaluationUpdate = (data) => {
      if (Number(data.company_id) === Number(companyId)) {
        console.log("🔔 Evaluation update received via Socket.");
        checkPendingEvaluations();
      }
    };

    SocketService.on("role_update", handlePermissionsUpdate);

    const handleSessionUpdate = (data) => {
      if (Number(data.company_id) === Number(companyId)) {
        if (data.action === "update" || data.action === "create") {
          console.log("🔔 Session timeout updated via Socket:", data.data.timeout_minutes);
          secureLocalStorage.setItem("sessionTimeoutMins", JSON.stringify(data.data.timeout_minutes));
        } else if (data.action === "status_update") {
          // If deactivated, revert to default 6h (360 mins)
          const newTimeout = data.data.status === 1 ? data.data.timeout_minutes : 360;
          secureLocalStorage.setItem("sessionTimeoutMins", JSON.stringify(newTimeout));
        }
      }
    };

    SocketService.on("session_settings_update", handleSessionUpdate);
    SocketService.on("evaluation_update", handleEvaluationUpdate);

    return () => {
      SocketService.off("role_update", handlePermissionsUpdate);
      SocketService.off("session_settings_update", handleSessionUpdate);
      SocketService.off("evaluation_update", handleEvaluationUpdate);
    };
  }, [loggedUser, companyId, checkPendingEvaluations]);

  const updateLocalPermissions = (newPermissions) => {
    try {
      // Update local storage
      let adminInfo = JSON.parse(secureLocalStorage.getItem("adminInfo"));
      if (adminInfo) {
        if (adminInfo && adminInfo.user && adminInfo.user.userdata) {
          adminInfo.user.userdata.user_permissions = newPermissions;
          secureLocalStorage.setItem("adminInfo", JSON.stringify(adminInfo));

          console.log("Permissions updated in local storage:", newPermissions);

          // Trigger force update
          window.dispatchEvent(new Event("permissionsUpdated"));
        }
      }
    } catch (e) {
      console.error("Failed to update permissions:", e);
    }
  };

  // Trigger check on mount
  useEffect(() => {
    checkPendingEvaluations();
  }, [checkPendingEvaluations]);

  return (
    <>
      <Routes>
        <Route exact path="/" element={<Login />} />
        <Route exact path="/home" element={<Welcome />} />
        <Route exact path="/career" element={<Career />} />
        <Route exact path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route exact path="/initial-reset-password" element={<InitialResetPassword />} />
        <Route exact path="/unauthorized" element={
          <div style={{ textAlign: 'center', padding: '100px' }}>
            <h1>Unauthorized Access</h1>
            <p>You do not have permission to view this page.</p>
            <button onClick={() => window.location.href = "/"}>Back to Login</button>
          </div>
        } />
      </Routes>

      <Main key={forceUpdate} />

      {showPopup && popupData && (
        <EvaluationPopup
          data={popupData}
          onClose={() => setShowPopup(false)}
        />
      )}
    </>
  );
};

export default App;
